import { useEffect, useRef, useState } from 'react'
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material'
import { useSearchParams } from 'react-router-dom'

import { lsRead, lsWrite, lsRemove, PERSIST_KEYS } from 'src/utils/local-storage'
import { LayoutContainer } from '../layout-container'
import { PageWrapper } from '../page-wrapper'
import { SearchBar } from './search-bar'
import { CollapsableComponent } from '../collapsible-section'
import { ProductListComponent } from './products-list'
import { AccountsListComponent } from './accounts-list'

import {
  // useDebouncedSearch,
  useGetPublicAccounts,
  useGetPublicProducts,
  usePagination,
} from 'src/hooks'
import { PaginationContext } from '../pagination-buttons/context/pagination.context'

import { TrackgoodIconSmall } from 'src/assets/icons/v2/TrackgoodSmallIcon'
import { HomeHeader, HomeWrapper, HomeDescription } from './home.styled'
import { MostViewedProducts } from './most-viewed-products'
import { PageSimpleFooter } from '../page-simple-footer'

export const defaultLimit = 20
export const defaultPage = 1
type SortOrder = 'ASC' | 'DESC'
const DEFAULT_SORT_BY = 'created_at'
const DEFAULT_SORT_ORDER = 'DESC'

export const HomeScreen = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const persistedSortBy = lsRead<string>(PERSIST_KEYS.sortBy)
  const persistedSortOrder = lsRead<SortOrder>(PERSIST_KEYS.sortOrder)
  const persistedAi = lsRead<boolean>(PERSIST_KEYS.aiSearch)

  const initialSortBy = searchParams.get('sortBy') ?? persistedSortBy ?? DEFAULT_SORT_BY
  const initialSortOrder =
    (searchParams.get('sortOrder') as SortOrder) ?? persistedSortOrder ?? DEFAULT_SORT_ORDER
  const initialQuery = searchParams.get('q') ?? ''
  const aiParam = searchParams.get('ai')
  const initialAi = aiParam != null ? aiParam === '1' : (persistedAi ?? false)

  const initialPageProducts = Number(searchParams.get('qp')) || defaultPage
  const initialPageAccounts = Number(searchParams.get('qa')) || defaultPage

  const [searchQuery, setSearchQuery] = useState(initialQuery)
  const [submittedQuery, setSubmittedQuery] = useState(initialQuery)
  const [aiSearchMode, setAiSearchMode] = useState(initialAi)
  const [submittedAi, setSubmittedAi] = useState(initialAi)
  const [sortBy, setSortBy] = useState(initialSortBy)
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder)

  const hadParamsOnLoadRef = useRef(searchParams.toString() !== '')

  const [hasInteracted, setHasInteracted] = useState(false)

  const { page, setPage, rowsPerPage, setRowsPerPage } = usePagination(
    initialPageProducts,
    defaultLimit,
  )

  const {
    page: pageAcc,
    setPage: setPageAcc,
    rowsPerPage: rowsPerPageAcc,
    setRowsPerPage: setRowsPerPageAcc,
  } = usePagination(initialPageAccounts, defaultLimit)

  // const debouncedSearchQuery = useDebouncedSearch(searchQuery, 500)

  const {
    data: accounts,
    // refetch: refetchAccounts,
    isFetching: isFetchingAccounts,
  } = useGetPublicAccounts(
    {
      search: submittedQuery,
      page: pageAcc,
      pageSize: rowsPerPageAcc,
      aiSearch: submittedAi && submittedQuery.trim() !== '',
      sortBy,
      sortOrder,
    },
    {
      enabled: submittedQuery.trim() !== '',
      keepPreviousData: true,
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    },
  )
  const {
    data: products,
    // refetch: refetchProducts,
    isFetching: isFetchingProducts,
  } = useGetPublicProducts(
    {
      search: submittedQuery,
      page: page,
      pageSize: rowsPerPage,
      sortBy,
      sortOrder,
    },
    {
      enabled: !(submittedAi && submittedQuery.trim() !== ''),
      keepPreviousData: true,
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    },
  )

  useEffect(() => {
    const rawQ = submittedQuery
    const rawAi = submittedAi ? '1' : '0'
    const rawSortBy = sortBy
    const rawSortOrder = sortOrder
    const rawPageProducts = page
    const rawPageAccounts = pageAcc

    const isDefault =
      rawQ === '' &&
      rawAi === '0' &&
      rawSortBy === DEFAULT_SORT_BY &&
      rawSortOrder === DEFAULT_SORT_ORDER &&
      rawPageProducts === defaultPage &&
      rawPageAccounts === defaultPage

    if (!hadParamsOnLoadRef.current && !hasInteracted && isDefault) return

    const next = new URLSearchParams(searchParams)

    // q
    if (rawQ === '') next.delete('q')
    else next.set('q', rawQ)

    // ai
    if (rawAi === '0') next.delete('ai')
    else next.set('ai', rawAi)

    // sortBy
    if (rawSortBy === DEFAULT_SORT_BY) next.delete('sortBy')
    else next.set('sortBy', rawSortBy)

    // sortOrder
    if (rawSortOrder === DEFAULT_SORT_ORDER) next.delete('sortOrder')
    else next.set('sortOrder', rawSortOrder)

    // product page
    if (rawPageProducts === defaultPage) next.delete('qp')
    else next.set('qp', String(rawPageProducts))

    // account page

    if (rawPageAccounts === defaultPage) next.delete('qa')
    else next.set('qa', String(rawPageAccounts))

    // only update if something actually changed
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
  }, [
    submittedQuery,
    submittedAi,
    sortBy,
    sortOrder,
    hasInteracted,
    searchParams,
    setSearchParams,
    page,
    pageAcc,
  ])

  useEffect(() => {
    if (!submittedAi && sortBy === 'relevance') {
      setSortBy(DEFAULT_SORT_BY)
    }
  }, [submittedAi])

  // useEffect(() => {
  //   if (submittedQuery.trim() !== '') {
  //     if (!submittedAi) {
  //       refetchProducts()
  //     }
  //     refetchAccounts()
  //   }
  // }, [submittedQuery, submittedAi, sortBy, sortOrder])

  // useEffect(() => {
  //   if (submittedQuery === '') lsRemove(PERSIST_KEYS.searchQuery)
  //   else lsWrite(PERSIST_KEYS.searchQuery, submittedQuery)
  // }, [submittedQuery])

  useEffect(() => {
    if (!submittedAi) lsRemove(PERSIST_KEYS.aiSearch)
    else lsWrite(PERSIST_KEYS.aiSearch, true)
  }, [submittedAi])

  const productPaginationValues = {
    count: products?.total ?? 0,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    isShown: !!products && products.total > rowsPerPage,
  }

  const accountPaginationValues = {
    count: accounts?.total ?? 0,
    page: pageAcc,
    setPage: setPageAcc,
    rowsPerPage: rowsPerPageAcc,
    setRowsPerPage: setRowsPerPageAcc,
    isShown: !!accounts && accounts.total > rowsPerPageAcc,
  }

  const handleSearch = () => {
    setHasInteracted(true)

    if (page !== defaultPage) setPage(defaultPage)
    if (pageAcc !== defaultPage) setPageAcc(defaultPage)

    setSubmittedQuery(searchQuery.trim())
    setSubmittedAi(aiSearchMode)
  }

  const handleAiToggle = (val: boolean) => {
    setHasInteracted(true)
    setAiSearchMode(val)
  }

  const handleSortByChange = (e: SelectChangeEvent<string>) => {
    const next = e.target.value

    setHasInteracted(true)
    setSortBy(next)

    setPage(defaultPage)
    setPageAcc(defaultPage)

    if (next === DEFAULT_SORT_BY) lsRemove(PERSIST_KEYS.sortBy)
    else lsWrite(PERSIST_KEYS.sortBy, next)
  }

  const handleSortOrderChange = (e: SelectChangeEvent<SortOrder>) => {
    const next = e.target.value

    setHasInteracted(true)
    setSortOrder(e.target.value as SortOrder)

    setPage(defaultPage)
    setPageAcc(defaultPage)

    if (next === DEFAULT_SORT_ORDER) lsRemove(PERSIST_KEYS.sortOrder)
    else lsWrite(PERSIST_KEYS.sortOrder, next)
  }

  const isLoading = isFetchingAccounts || isFetchingProducts

  // Calculate counts for labels
  const companiesCount =
    submittedAi && submittedQuery.trim() !== ''
      ? (accounts?.aiAccounts?.length ?? 0)
      : (accounts?.total ?? 0)
  const productsCount =
    submittedAi && submittedQuery.trim() !== ''
      ? (accounts?.aiProducts?.length ?? 0)
      : (products?.total ?? 0)

  return (
    <LayoutContainer>
      <PageWrapper>
        <HomeWrapper id="index__components-home__homewrapper__l275">
          <TrackgoodIconSmall />

          <HomeHeader id="index__components-home__homeheader__l278">
            Explore verified product journeys from transparent brands around the world
          </HomeHeader>

          <HomeDescription id="index__components-home__homedescription__l282">
            This is the home of Trackgood-verified brands and products—where supply chains are
            transparent, and green claims are backed by blockchain and AI.
          </HomeDescription>

          <SearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            isAiSearch={aiSearchMode}
            setIsAiSearch={handleAiToggle}
            onSearch={handleSearch}
            isLoading={isLoading}
          />

          <Box id="index__components-home__box__l296" sx={{ width: '100%', mt: 2 }}>
            <Stack
              id="index__components-home__stack__l297"
              direction="row"
              spacing={2}
              alignItems="center"
            >
              <FormControl id="index__components-home__formcontrol__l298" size="small">
                <InputLabel id="sort-by-label">Sort by</InputLabel>
                <Select
                  id="index__components-home__select__l300"
                  labelId="sort-by-label"
                  value={sortBy}
                  label="Sort by"
                  onChange={handleSortByChange}
                >
                  {/* <MenuItem disabled={!aiSearchMode} value="relevance">
                       Relevance
                     </MenuItem> */}
                  <MenuItem id="index__components-home__menuitem__l309" value="name">
                    Name
                  </MenuItem>
                  <MenuItem id="index__components-home__menuitem__l310" value="created_at">
                    Created on
                  </MenuItem>
                  {/* <MenuItem value="updated_at">Updated At</MenuItem> */}
                </Select>
              </FormControl>

              <FormControl id="index__components-home__formcontrol__l315" size="small">
                <InputLabel id="sort-order-label">Order</InputLabel>
                <Select
                  id="index__components-home__select__l317"
                  labelId="sort-order-label"
                  value={sortOrder}
                  label="Order"
                  onChange={handleSortOrderChange}
                >
                  <MenuItem id="index__components-home__menuitem__l323" value="ASC">
                    Ascending
                  </MenuItem>
                  <MenuItem id="index__components-home__menuitem__l324" value="DESC">
                    Descending
                  </MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>

          <Box
            id="index__components-home__box__l330"
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              boxSizing: 'border-box',
              pt: '15px',
            }}
          >
            {!submittedQuery && <MostViewedProducts />}

            {!!submittedQuery && !submittedAi && (
              <CollapsableComponent defaultOpen label={`Companies (${companiesCount})`}>
                <PaginationContext.Provider value={accountPaginationValues}>
                  <AccountsListComponent accounts={accounts?.items || []} withPagination />
                </PaginationContext.Provider>
              </CollapsableComponent>
            )}

            {!!submittedQuery && submittedAi && (
              <CollapsableComponent defaultOpen label={`Companies (${companiesCount})`}>
                <PaginationContext.Provider value={accountPaginationValues}>
                  <AccountsListComponent accounts={accounts?.aiAccounts || []} />
                </PaginationContext.Provider>
              </CollapsableComponent>
            )}

            {!(submittedAi && submittedQuery.trim() !== '') && (
              <CollapsableComponent defaultOpen label={`Products (${productsCount})`}>
                <PaginationContext.Provider value={productPaginationValues}>
                  <ProductListComponent products={products?.items || []} withPagination />
                </PaginationContext.Provider>
              </CollapsableComponent>
            )}

            {submittedAi && submittedQuery.trim() !== '' && (
              <CollapsableComponent defaultOpen label={`Products (${productsCount})`}>
                <PaginationContext.Provider value={productPaginationValues}>
                  <ProductListComponent products={accounts?.aiProducts || []} />
                </PaginationContext.Provider>
              </CollapsableComponent>
            )}
          </Box>
        </HomeWrapper>
      </PageWrapper>

      <PageSimpleFooter />
    </LayoutContainer>
  )
}
